import List from 'rc-virtual-list';
import { ColumnSchema } from '@client/new/components/StandardTable/StandardTable';
import {
  F0,
  TypedValue,
  ValueOf,
  toTypedValueConstructor,
} from '@client/new/helpers/types';
import {
  Typography,
  TypographyProps,
} from '@design-system/components/typography/typography';
import { Checkbox } from '@mui/material';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import {
  CellCTAItems,
  CellCTAProps,
  CellMenu,
  MenuItems,
} from '@client/new/components/TableShared/Cell';
import { cell, row } from './TableList.css';
import { clsx } from 'clsx';
import { sprinkles } from '@design-system/sprinkles.css';
import { useBreakpoints } from '@design-system/breakpointsStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons';
import { TableLoader } from '@client/new/components/TableShared/TableLoader';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faSquare } from '@fortawesome/pro-light-svg-icons';
import { Badge } from '@design-system/components/badge';
import { Row as DSRow } from '@design-system/components/grid/grid';
import { Tooltip } from '@design-system/components/tooltip/tooltip';

export type TableListCell =
  | TypedValue<
      'header',
      {
        imageUrl?: string;
        showImgPlaceholder?: boolean;
        icon?: IconProp;
        title: string;
        subtitle?: string;
        onClick?: F0;
        tags?: Array<{
          icon?: IconProp;
          label: string;
          className?: string;
          readonly?: boolean;
          tooltip?: string;
        }>;
      }
    >
  | TypedValue<'tag', string>
  | TypedValue<'text', { text: string; className?: string } & TypographyProps>
  | TypedValue<'custom', JSX.Element>
  | TypedValue<'actions', Array<CellCTAProps>>
  | TypedValue<'menu', MenuItems>;

export type TableListRow = {
  id: string;
  cells: TableListCell[];
};

const Tag = (p: PropsWithChildren) => (
  <div
    style={{
      background: '#eee',
      borderRadius: '30px',
      padding: '4px 16px',
      textAlign: 'center',
    }}
  >
    <Typography variant="body" size="medium" as="span">
      {p.children}
    </Typography>
  </div>
);

const Header = (p: ValueOf<TableListCell, 'header'>) => (
  <div
    className={clsx(
      'flex flex-row w-full min-w-0',
      p?.onClick && 'cursor-pointer',
    )}
    onClick={p.onClick}
  >
    {p.showImgPlaceholder ? (
      <div className="mr-4 min-w-[52px] min-h-[52px] bg-gray-200 rounded-[10px]"></div>
    ) : !p.imageUrl ? null : (
      <div className="mr-4 w-[68px] h-[68px] p-2 flex justify-center items-center">
        <img
          src={p.imageUrl}
          className={clsx(
            sprinkles({ clipPath: 'sm' }),
            'max-w-[52px] max-h-[52px]',
          )}
        />
      </div>
    )}
    {!p.icon ? null : (
      <div className="flex justify-center items-center">
        <FontAwesomeIcon icon={p.icon} className="w-4 h-4 mr-2" />
      </div>
    )}
    <div className="flex justify-between items-center w-full min-w-0 mr-4">
      <div className="flex flex-col justify-center min-w-0 flex-1">
        <Typography
          variant="body"
          size="medium"
          color="N-100"
          className="overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {p.title}
        </Typography>
        <DSRow>
          {!p.subtitle ? null : (
            <Typography variant="body" size="small" color="N-60">
              {p.subtitle}
            </Typography>
          )}
          {!p.tags ? null : (
            <div className="flex items-center">
              {p.tags.map((tag) => (
                <Tooltip
                  key={tag.label}
                  tooltipProps={{
                    maxWidth: 195,
                  }}
                  variant="dark"
                  title={tag.tooltip}
                >
                  <Badge label={tag.label} startIcon={tag.icon} />
                </Tooltip>
              ))}
            </div>
          )}
        </DSRow>
      </div>
    </div>
  </div>
);

const cellRenderer = (p: TableListCell): JSX.Element => {
  switch (p.type) {
    case 'header':
      return <Header {...p.value} />;
    case 'tag':
      return <Tag>{p.value}</Tag>;
    case 'text': {
      const { text, className, ...props } = p.value;
      return (
        <Typography
          size="medium"
          variant="body"
          {...props}
          className={className}
        >
          {text}
        </Typography>
      );
    }
    case 'actions':
      return <CellCTAItems items={p.value} />;
    case 'menu':
      return <CellMenu items={p.value} />;
    case 'custom':
      return p.value;
  }
};

const Cell = ({
  data,
  schema,
}: {
  data: TableListCell;
  schema: ColumnSchema;
}): JSX.Element => {
  return (
    <div className={clsx(cell, sprinkles({ textAlign: schema.justify }))}>
      {cellRenderer(data)}
    </div>
  );
};

export const toCell = toTypedValueConstructor<TableListCell>();

const toCSSSize = (v?: number | string, def = '100%') =>
  v ? (typeof v === 'string' ? v : v + 'px') : def;

export const toGridTemplateColumns = (v: Array<ColumnSchema>) =>
  v
    .map((e) =>
      e.width === 'stretchable'
        ? 'auto'
        : e.maxWidth
        ? `minmax(${toCSSSize(e.width)}, ${toCSSSize(e.maxWidth)})`
        : toCSSSize(e.width),
    )
    .join(' ');

export const Row = (p: {
  cells: TableListCell[];
  schema: Array<ColumnSchema>;
  style?: React.CSSProperties;
  itemHeight?: string | number;
  itemGap?: string | number;
  rowClassName?: string;
}) => (
  <div
    className={clsx(row, p?.rowClassName)}
    style={{
      marginBottom: toCSSSize(p.itemGap),
      gridTemplateColumns: toGridTemplateColumns(p.schema),
      height: toCSSSize(p.itemHeight),
      ...p.style,
    }}
  >
    {p.cells.map((v, i) => (
      <Cell data={v} key={i} schema={p.schema[i]} />
    ))}
  </div>
);

export const TableList = (p: {
  rows: TableListRow[];
  schema: Array<ColumnSchema>;
  itemHeight: number;
  itemGap: number;
  hideHeader?: boolean;
  listHeight?: number;
  rowClassName?: string;
  withSelection?: {
    onSelect: (value: any) => void;
    selectedIds: string[];
  };
  loading?: boolean;
}) => {
  const [h, setH] = useState(0);
  const isMobile = useBreakpoints((s) => s.mobile);
  const updateSize = () => {
    const nh = window.innerHeight - (isMobile ? 250 : 300);
    setH(nh - (nh % 100));
  };
  useEffect(() => {
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  if (p?.loading) {
    return <TableLoader rows={5} height={130} />;
  }

  return (
    <div className="overscroll-contain">
      {p.hideHeader ? null : (
        <div
          style={{
            display: 'grid',
            padding: '0 16px',
            gridTemplateColumns: toGridTemplateColumns(p.schema),
          }}
        >
          {p.schema.map((v, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: v.justify,
              }}
            >
              <Typography variant="body" size="small" color="N-100">
                {v.label}
              </Typography>
            </div>
          ))}
        </div>
      )}
      <List
        data={p.rows}
        itemHeight={p.itemHeight + p.itemGap}
        itemKey="id"
        height={h}
        // className={clsx(p?.rowClassName)}
      >
        {(v) => (
          <div className="flex items-center">
            {!p?.withSelection ? null : (
              <Checkbox
                className="mb-4"
                checkedIcon={
                  <FontAwesomeIcon icon={faSquareCheck} className="w-5 h-5" />
                }
                icon={
                  <FontAwesomeIcon
                    icon={faSquare}
                    className="w-5 h-5 text-black"
                  />
                }
                checked={p.withSelection.selectedIds.includes(v.id)}
                onChange={() => p.withSelection?.onSelect(v.id)}
              />
            )}
            <Row
              key={v.id}
              cells={v.cells}
              schema={p.schema}
              itemHeight={p.itemHeight}
              itemGap={p.itemGap}
              rowClassName={p?.rowClassName}
            />
          </div>
        )}
      </List>
    </div>
  );
};
